/**
 * Two export formats.
 *
 * 1. **The SLCPT/1 block** — a fixed-field text digest, roughly 150 tokens for a
 *    nine-measure exercise, designed so an AI grader can judge *style* without
 *    re-deriving a single interval. The arithmetic is done; only judgment is
 *    asked for. It round-trips: `parseBlock` reads one back.
 * 2. **The lesson file** (`.slcpt.json`) — the whole session, restorable.
 */

import type { AssistId, Edo, Exercise, GradeReport, RulesProfile } from './types';
import { measureCount } from './types';
import { analyze } from './analysis';
import { edoOf, nameOfPitch, parsePitchName } from './pitch';
import { keyById } from './keys';

/** Fields carried alongside the exercise in a save. */
export interface SessionMeta {
  readonly profile: RulesProfile;
  readonly singleStaff: boolean;
  /** Which assists were engaged at some point while notes were placed. */
  readonly assisted: Readonly<Record<AssistId, boolean>>;
  /** Which assists are engaged right now. */
  readonly assists: Readonly<Record<AssistId, boolean>>;
}

/* ---- the AI-gradable block --------------------------------------------- */

/**
 * Render the compact grading block. `report` is optional — without it the block
 * still carries the analysis, marked incomplete.
 */
export function buildBlock(ex: Exercise, meta: SessionMeta, report: GradeReport | null): string {
  const n = measureCount(ex);
  const A = report ? report.analysis : analyze(ex, meta.profile);
  const cell = (t: string | number) => String(t).padEnd(4);
  const row = (label: string, cells: (string | number)[]) =>
    `${label}: ${cells.map(cell).join('')}`.trimEnd();

  const assisted = (Object.keys(meta.assisted) as AssistId[]).filter((k) => meta.assisted[k]);
  const lines = [
    `SLCPT/1 species=1 rules=${meta.profile} edo=${edoOf(ex)} key=${ex.key.id} cf=${ex.cfPos} m=${n} assists=${
      assisted.length ? assisted.join(',') : 'none'
    }`,
    row('m ', Array.from({ length: n }, (_, i) => i + 1)),
    row('cf', ex.cf.map((pitch) => nameOfPitch(pitch, edoOf(ex)))),
    row('cp', ex.cp.map((pitch) => (pitch == null ? '·' : nameOfPitch(pitch, edoOf(ex))))),
    row('iv', A.intervals.map((v) => (v == null ? '·' : v.num + (v.consonant ? '' : '*')))),
    row('mo', ['-', ...A.motions.map((m) => m ?? '·')]),
  ];

  const errors = A.findings.filter((f) => f.severity === 'error').map((f) => f.message);
  const warnings = A.findings.filter((f) => f.severity === 'warning').map((f) => f.message);
  let auto = `auto: ${errors.length || warnings.length ? [...errors, ...warnings].join('; ') : 'clean'}`;
  auto += report && report.complete ? `; score ${report.total}/20` : '; incomplete';
  lines.push(auto);
  return lines.join('\n');
}

/**
 * Read a block back into an exercise. Only the header and the `cf`/`cp` rows
 * are needed; everything else is re-derived.
 */
export function parseBlock(text: string): { exercise: Exercise; profile: RulesProfile } | null {
  const lines = text.trim().split(/\r?\n/);
  const header = lines.find((l) => l.startsWith('SLCPT/'));
  if (!header) return null;
  const field = (name: string) => header.match(new RegExp(`\\b${name}=(\\S+)`))?.[1];

  const key = keyById(field('key'));
  const edo: Edo = field('edo') === '31' ? 31 : 12;
  const cfPos = field('cf') === 'upper' ? 'upper' : 'lower';
  const profile: RulesProfile = field('rules') === 'gran' ? 'gran' : 'lim';

  const cells = (label: string) =>
    lines.find((l) => l.startsWith(`${label}:`))?.slice(label.length + 1).trim().split(/\s+/) ?? [];

  const cfRow = cells('cf');
  const cpRow = cells('cp');
  if (!cfRow.length) return null;

  const cf = cfRow.map((name) => parsePitchName(name, edo));
  const cp = cf.map((_, i) => {
    const token = cpRow[i];
    return !token || token === '·' ? null : parsePitchName(token, edo);
  });
  if (cf.some(Number.isNaN) || cp.some((pitch) => pitch != null && Number.isNaN(pitch))) return null;
  return { exercise: { edo, key, cfPos, cf, cp }, profile };
}

/* ---- the lesson file ---------------------------------------------------- */

/** The on-disk shape of a `.slcpt.json` lesson. */
export interface LessonFile {
  readonly app: 'slcpt';
  readonly version: 3;
  readonly saved: string;
  readonly edo: Edo;
  readonly key: string;
  readonly cfPos: 'lower' | 'upper';
  readonly cf: readonly number[];
  readonly cp: readonly (number | null)[];
  readonly profile: RulesProfile;
  readonly singleStaff: boolean;
  readonly assisted: Record<AssistId, boolean>;
  readonly assists: Record<AssistId, boolean>;
  readonly grade: { total: number; max: number } | null;
  readonly block: string;
}

/** Serialize the current session. */
export function buildLesson(
  ex: Exercise,
  meta: SessionMeta,
  report: GradeReport | null,
  now: Date = new Date(),
): LessonFile {
  return {
    app: 'slcpt',
    version: 3,
    saved: now.toISOString(),
    edo: edoOf(ex),
    key: ex.key.id,
    cfPos: ex.cfPos,
    cf: ex.cf,
    cp: ex.cp,
    profile: meta.profile,
    singleStaff: meta.singleStaff,
    assisted: { ...meta.assisted },
    assists: { ...meta.assists },
    grade: report && report.complete ? { total: report.total, max: report.max } : null,
    block: buildBlock(ex, meta, report),
  };
}

/**
 * Read a lesson file. Older saves default to 12-EDO. Version 1 used `ghost` for
 * the allowed-notes assist and `strict` for the Lim profile.
 */
export function parseLesson(json: unknown): { exercise: Exercise; meta: SessionMeta } | null {
  const j = json as Record<string, any>;
  if (!j || j.app !== 'slcpt' || !Array.isArray(j.cf)) return null;
  const key = keyById(j.key);
  const edo: Edo = j.edo === 31 ? 31 : 12;
  const cf: number[] = j.cf.map(Number);
  const cp: (number | null)[] = Array.isArray(j.cp)
    ? cf.map((_, i) => (j.cp[i] == null ? null : Number(j.cp[i])))
    : cf.map(() => null);

  const legacyAssist = (source: any): Record<AssistId, boolean> => ({
    labels: !!source?.labels,
    allowed: !!(source?.allowed ?? source?.ghost),
  });

  return {
    exercise: { edo, key, cfPos: j.cfPos === 'upper' ? 'upper' : 'lower', cf, cp },
    meta: {
      profile: j.profile === 'gran' ? 'gran' : 'lim',
      singleStaff: !!(j.singleStaff ?? j.oneStaff),
      assisted: legacyAssist(j.assisted),
      assists: j.assists || j.clutches ? legacyAssist(j.assists ?? j.clutches) : { labels: true, allowed: true },
    },
  };
}

/** Filename for a save, e.g. `slcpt-Dm-2026-08-10.json`. */
export const lessonFilename = (ex: Exercise, now: Date = new Date()): string =>
  `slcpt-${ex.key.id}-${now.toISOString().slice(0, 10)}.json`;
